/**
 * The overview's small pure pieces: a chart's day labels, a check's status in
 * words and colour, a quiet restaurant's line. Tested in `overview-words.test.ts`.
 */

import type { HealthStatus } from '@restaurant/api-client';

/**
 * A trading day as a chart label: "29 Sep", "Sep 29", "29. Sept." — whatever
 * the reader's locale writes. Formatted in UTC so the day never slips (see
 * `formatDay` in words.ts).
 */
export function shortDay(isoDate: string, locale: string): string {
  const [year, month, day] = isoDate.split('-').map(Number);
  if (!year || !month || !day) return isoDate;
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, month - 1, day)));
}

export function statusWords(status: HealthStatus): string {
  if (status === 'problem') return 'Problem';
  if (status === 'warning') return 'Warning';
  return 'OK';
}

export function statusTone(status: HealthStatus): 'ok' | 'warn' | 'danger' {
  if (status === 'problem') return 'danger';
  if (status === 'warning') return 'warn';
  return 'ok';
}

/** The headline over the health list. */
export function healthHeadline(
  status: HealthStatus,
  problems: number,
  warnings: number,
): string {
  if (status === 'ok') return 'Everything is working.';
  const parts: string[] = [];
  if (problems > 0) parts.push(`${problems} ${problems === 1 ? 'problem' : 'problems'}`);
  if (warnings > 0) parts.push(`${warnings} ${warnings === 1 ? 'warning' : 'warnings'}`);
  return `${parts.join(' and ')} to look at.`;
}

export function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

/** A page's address as its name: "/" is the home page. */
export function pageName(path: string): string {
  if (path === '/') return 'Home';
  return path;
}

/** "fr" as "French", in the operator's language; "?" as "Not sent". */
export function languageName(code: string, locale: string): string {
  if (code === '?') return 'Not sent';
  try {
    return new Intl.DisplayNames([locale], { type: 'language' }).of(code) ?? code;
  } catch {
    return code;
  }
}
